import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { cva } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn.js';

const navVariants = cva('space-y-1', {
  variants: {
    variant: {
      default: 'px-4',
      compact: 'px-2',
      minimal: 'px-1',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const itemVariants = cva(
  'group relative flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-300 ease-in-out',
  {
    variants: {
      isActive: {
        true: 'from-primary-80 to-primary-100 shadow-primary-80/25 scale-[1.02] transform bg-gradient-to-r text-white shadow-lg dark:from-orange-500 dark:to-orange-600 dark:shadow-orange-500/25',
        false:
          'text-secondary-80 hover:bg-secondary-10/70 hover:text-primary-100 hover:scale-[1.01] dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-orange-400',
      },
    },
    defaultVariants: {
      isActive: false,
    },
  },
);

const iconContainerVariants = cva(
  'flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200',
  {
    variants: {
      isActive: {
        true: 'bg-white/20',
        false:
          'bg-secondary-10 group-hover:bg-primary-10 dark:bg-gray-700 dark:group-hover:bg-orange-900/50',
      },
    },
    defaultVariants: {
      isActive: false,
    },
  },
);

const iconColorVariants = cva('transition-all duration-200', {
  variants: {
    isActive: {
      true: 'text-white',
      false:
        'text-secondary-60 group-hover:text-primary-100 dark:text-gray-300 dark:group-hover:text-orange-400',
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

const badgeVariants = cva('rounded-full px-2 py-0.5 text-xs font-bold', {
  variants: {
    isActive: {
      true: 'bg-white/20 text-white',
      false: '',
    },
    badgeType: {
      new: '',
      default: '',
    },
  },
  compoundVariants: [
    {
      isActive: false,
      badgeType: 'new',
      class:
        'bg-light-green-10 text-light-green-100 dark:bg-green-900/50 dark:text-green-400',
    },
    {
      isActive: false,
      badgeType: 'default',
      class:
        'bg-primary-10 text-primary-100 dark:bg-orange-900/50 dark:text-orange-400',
    },
  ],
  defaultVariants: {
    isActive: false,
    badgeType: 'default',
  },
});

const collapsedButtonVariants = cva(
  'relative flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl transition-all duration-300 ease-in-out hover:scale-105',
  {
    variants: {
      isActive: {
        true: 'from-primary-80 to-primary-100 shadow-primary-80/25 bg-gradient-to-r text-white shadow-lg dark:from-orange-500 dark:to-orange-600 dark:shadow-orange-500/25',
        false:
          'bg-secondary-10/50 text-secondary-60 hover:bg-secondary-10 hover:text-primary-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-orange-400',
      },
    },
    defaultVariants: {
      isActive: false,
    },
  },
);

const TOOLTIP_CLASSES =
  'invisible absolute top-1/2 left-full z-50 ml-2 -translate-y-1/2 transform rounded-lg px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 bg-secondary-90 dark:bg-gray-800';

const NavigationMenu = ({
  items = [],
  activeItem = null,
  onItemClick,
  className,
  variant = 'default',
  showBadges = true,
  showActiveIndicator = true,
  isCollapsed = false,
  ...props
}) => {
  const isItemActive = (item) => {
    if (!activeItem) return false;
    // Extract base path from item.path (remove query params)
    const itemBasePath = item.path?.split('?')[0];
    // Check if activeItem matches the base path
    return (
      activeItem === itemBasePath ||
      activeItem === item.path ||
      activeItem === item.id
    );
  };

  if (isCollapsed) {
    return (
      <nav className={cn(navVariants({ variant }), className)} {...props}>
        <div className="flex flex-col items-center space-y-2 px-2">
          {items.map((item) => {
            const isActive = isItemActive(item);
            const IconComponent = item.icon;

            return (
              <div key={item.id} className="group relative">
                <button
                  onClick={() => onItemClick && onItemClick(item)}
                  className={collapsedButtonVariants({ isActive })}
                  title={item.title}
                >
                  {typeof IconComponent === 'string' ? (
                    <i
                      className={cn(
                        IconComponent,
                        'flex items-center justify-center text-lg',
                      )}
                    />
                  ) : (
                    <div className="flex items-center justify-center">
                      <IconComponent size={20} />
                    </div>
                  )}

                  {showBadges && item.badge && (
                    <span className="bg-error-100 absolute -top-1 -left-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-bold text-white dark:bg-red-500">
                      {item.badge === 'جدید' ? '!' : item.badge}
                    </span>
                  )}
                </button>

                <div className={TOOLTIP_CLASSES}>
                  {item.title}
                  <div className="border-r-secondary-90 absolute top-1/2 left-full -translate-y-1/2 transform border-4 border-transparent dark:border-r-gray-800" />
                </div>
              </div>
            );
          })}
        </div>
      </nav>
    );
  }

  return (
    <nav className={cn(navVariants({ variant }), className)} {...props}>
      <ul className="space-y-1">
        {items.map((item) => {
          const isActive = isItemActive(item);
          const IconComponent = item.icon;

          return (
            <li key={item.id}>
              <Link
                to={item.path || '#'}
                onClick={() => onItemClick && onItemClick(item)}
                className={itemVariants({ isActive })}
              >
                <div className="flex items-center space-x-3 space-x-reverse">
                  <div className={iconContainerVariants({ isActive })}>
                    {typeof IconComponent === 'string' ? (
                      <i
                        className={cn(
                          IconComponent,
                          'flex items-center justify-center text-sm',
                          iconColorVariants({ isActive }),
                        )}
                      />
                    ) : (
                      <div
                        className={cn(
                          'flex items-center justify-center',
                          iconColorVariants({ isActive }),
                        )}
                      >
                        <IconComponent size={18} />
                      </div>
                    )}
                  </div>
                  <span className="text-right text-sm font-medium">
                    {item.title}
                  </span>
                </div>

                {showBadges && item.badge && (
                  <div
                    className={badgeVariants({
                      isActive,
                      badgeType: item.badge === 'جدید' ? 'new' : 'default',
                    })}
                  >
                    {item.badge}
                  </div>
                )}

                {showActiveIndicator && isActive && (
                  <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-white shadow-lg shadow-white/50" />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

NavigationMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      path: PropTypes.string,
      badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  activeItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onItemClick: PropTypes.func,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'compact', 'minimal']),
  showBadges: PropTypes.bool,
  showActiveIndicator: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

export default NavigationMenu;
